import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { cloneDeep, findIndex, each } from 'lodash';

import { USERS_PATHS } from '../users';
import { UsersService } from '../users.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { Enums } from '../../../configs/Enums';
import { PaginationOnLoad } from '../../../shared/pagination/pagination';
import { PaginationService } from '../../../shared/pagination/pagination.service';
import { SectorsService } from '../../sectors/sectors.service';
import { AuthService } from '../../auth/auth.service';
import { AuthGroupTypesEnum } from '../../auth/auth-group-types.enum';
import { environment } from '../../../../environments/environment.prod';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  standalone: false,
})
export class UsersComponent implements OnInit {
  public moment = require('moment');
  public USERS_PATHS = USERS_PATHS;
  private usersBreadcrumbs = [{ text: 'Сотрудники', url: null }];
  public filterData = {
    data: {
      usersList: null,
      regionsList: null,
      branchesList: null,
      sectorsList: null,
      companiesList: null,
      statusesList: null,
      rolesList: null,
    },
    selected: {
      userSelected: null,
      phone: null,
      regionsSelected: [],
      branchesSelected: [],
      sectorsSelected: [],
      companiesSelected: [],
      statusSelected: null,
      rolesSelected: [],
      employee_number: null,
      personnel_number: null,
      isVisibleHiddenUsers: false,
    },
    params: {
      full_name: '',
      employee_id: '',
      phone: '',
      mb_regions: [],
      mb_ids: [],
      sectors_ids: [],
      partnerships_ids: [],
      employee_status: '',
      groups_ids: [],
      emp_number: '',
      u_id: '',
      personnel_number: '',
    },
    onChangeRegions: this.onChangeRegions.bind(this),
    onChangeBranches: this.onChangeBranches.bind(this),
    onChangeSectors: this.onChangeSectors.bind(this),
    onChangeCompanies: this.onChangeCompanies.bind(this),
    onChangeRoles: this.onChangeRoles.bind(this),
    onApplyFilter: this.onApplyUsersFilter.bind(this),
    onResetFilter: this.onResetUsersFilter.bind(this),
  };
  private filterDataDefault = cloneDeep(this.filterData);
  private filterDataSelectedDefault = cloneDeep(this.filterData.selected);
  private filterDataParamsDefault = cloneDeep(this.filterData.params);
  public userDataIsLoaded = false;
  public userExpanded = null;
  public userListFiltered = null;
  public paginationBlock = {
    limits: [25, 50, 100],
    limitSelected: 25,
    limitCountMaxRows: 0,
    offset: 0,
    countLoaded: 0,
    onLoadPartial: this.onLoadPartial.bind(this),
  };
  public paginationBlockDefault = cloneDeep(this.paginationBlock);
  public usersDataLoading = false;
  public emptyMessage = null;
  public userNumber;
  public isOneUserDisplay;
  public dateShortFormat = Enums.dates.shortFormat;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private paginationService: PaginationService,
    private sectorsService: SectorsService,
    private breadcrumbsService: BreadcrumbsService,
    public userService: UsersService,
    private authService: AuthService
  ) {}

  // Вкладка «Квалификации» доступна только администратору
  public get isAdministrator(): boolean {
    return this.authService.is(AuthGroupTypesEnum.Admin);
  }

  ngOnInit() {
    this.breadcrumbsService.updateBreadcrumbs(this.usersBreadcrumbs);
    this.paginationService.initPagination(this.paginationBlock);
    this.getFilterData();

    this.route.params.subscribe((params) => {
      if (params['u_id']) {
        this.userNumber = params['u_id'];
        this.isOneUserDisplay = !!this.userNumber;
        this.filterData.params.u_id = this.userNumber;
        this.getUsers(0, this.paginationBlock.limitSelected);
      }
    });
  }

  private getFilterData(): void {
    this.usersDataLoading = true;
    this.userService.getFilterDataUnion().subscribe((response) => {
      this.filterData.data.regionsList = response[0]['data'].map((region) => {
        return { name: region };
      });
      this.filterData.data.branchesList = response[1]['data'];
      this.filterData.data.sectorsList = response[2]['data'].map((s) => {
        if (!s.is_visible) {
          s.sector_number_str = '<span class="app-combobox__item--highlight-part">' + s.sector_number_str + '</span>';
        }
        return s;
      });
      this.filterData.data.companiesList = response[3]['data'].map((company) => {
        const companyName = company.ownership_type.ot_short + ' "' + company.ps_name + '"';

        if (!company.is_visible) {
          company.new_name = '<span class="app-combobox__item--highlight-part">' + companyName + '</span>';
        } else {
          company.new_name = companyName;
        }

        return company;
      });
      this.filterData.data.statusesList = response[4]['data'];
      this.filterData.data.rolesList = response[5]['data'];

      this.initUIdata();

      this.usersDataLoading = false;
    });
  }

  private getBranches(): void {
    this.sectorsService
      .getBranches(this.filterData.params.mb_regions && this.filterData.params.mb_regions.length ? { mb_regions: this.filterData.params.mb_regions } : null)
      .subscribe(
        (response) => {
          this.filterData.data.branchesList = response['data'];
        },
        (error) => {
          console.log(error);
          this.filterData.data.branchesList = null;
        }
      );
  }

  private getSectors(): void {
    const filterParams = this.filterData.params;
    const requestParams = {
      mb_ids: filterParams.mb_ids || '',
      mb_regions: filterParams.mb_regions || '',
      partnerships_ids: filterParams.partnerships_ids || '',
    };
    this.sectorsService.getFilterPlots(requestParams).subscribe(
      (response) => {
        this.filterData.data.sectorsList = response['data'];
      },
      (error) => {
        console.log(error);
        this.filterData.data.sectorsList = null;
      }
    );
  }

  public getUsers(offset: number, limit: number) {
    this.usersDataLoading = true;
    let params = {
      full_name: this.filterData.selected.userSelected || '',
      phone: this.filterData.selected.phone ? this.filterData.selected.phone : '',
      emp_number: this.filterData.selected.employee_number || '',
      personnel_number: this.filterData.selected.personnel_number || '',
      employee_status: this.filterData.selected.statusSelected
        && this.filterData.selected.statusSelected.es_id
        ? this.filterData.selected.statusSelected.es_id : '',
      limit: limit,
      offset: offset,
    };

    let paramsObj = Object.assign(this.filterData.params, params);
    each(paramsObj, (value, key) => {
      if (paramsObj[key] && paramsObj[key].forEach && !paramsObj[key].length) {
        paramsObj[key] = '';
      }
    });
    this.userService.getUsers(paramsObj).subscribe(
      (response) => {
        let count = response['data']['count'];
        let result = response['data']['result'].map((user) => {
          let hireDate = this.moment(user.employer.hire_date);
          let dismissalDate = this.moment(user.employer.dismissal_date || this.moment().format(this.dateShortFormat));

          user.employer.workDiff = user.employer.hire_date && dismissalDate ? Math.abs(dismissalDate.diff(hireDate, 'years')) : '';

          user.userExpandedDataLoading = false;

          return user;
        });

        if (offset > 0) {
          this.userListFiltered = this.userListFiltered.concat(result);
        } else {
          if (!this.userListFiltered) {
            this.userListFiltered = [];
          }

          this.userListFiltered = result;
        }

        if (result && result.length === 1 && this.isOneUserDisplay) {
          this.userExpanded = result[0];
          this.getUserExpandedData();
        }

        this.makeEmptyMessage();
        this.checkPartner();
        this.paginationService.updatePaginationLoadPartial(this.userListFiltered.length, count);

        this.userDataIsLoaded = true;
        this.usersDataLoading = false;
      },
      (error) => {
        console.log(error);
        this.usersDataLoading = false;
      }
    );
  }

  private checkPartner() {
    const checkRequest = this.userListFiltered
    .filter((u) => u && u.employee && u.employer) 
    .map((u) => {
      return {
        user_id: u.u_id,
        partner_code: u.employer.partner_code,
        personnel_number: u.employee.personnel_number,
      };
    });

    // this.userService.checkPartner({ data: checkRequest }).subscribe(
    //   (response) => {
    //     each(response['data'], (item) => {
    //       const index = findIndex(this.userListFiltered, (u) => u['u_id'] === item.user_id);
    //
    //       if (index > -1) {
    //         this.userListFiltered[index].employee.check_partner_message = item.message;
    //       }
    //     });
    //   },
    //   (error) => {
    //     console.log(error);
    //   }
    // );
  }

  private getUserExpandedData() {
    if (!this.userExpanded) {
      return;
    }
    this.userExpanded['userExpandedDataLoading'] = true;
    this.userService.getUserExpandedData(this.userExpanded.u_id).subscribe(
      (response) => {
        this.userExpanded = Object.assign(this.userExpanded, response['data']);

        let hireDate = this.moment(this.userExpanded.employer.hire_date);
        let dismissalDate = this.moment(this.userExpanded.employer.dismissal_date || this.moment().format(this.dateShortFormat));

        this.userExpanded.employer.workDiff = this.userExpanded.employer.hire_date && dismissalDate ? Math.abs(dismissalDate.diff(hireDate, 'years')) : '';

        this.userExpanded['userExpandedDataLoading'] = false;
      },
      (error) => {
        this.userExpanded['expanded_data'] = null;
        this.userExpanded['userExpandedDataLoading'] = false;
      }
    );
  }

  private initUIdata() {
    if (!this.userNumber) {
      let index = findIndex(this.filterData.data.statusesList, {
        es_id: 1,
      });

      if (index > -1) {
        this.filterData.selected.statusSelected = this.filterData.data.statusesList[index];
        this.filterData.params.employee_status = this.filterData.data.statusesList[index].es_id;
      }
    }
  }

  public createUser() {
    this.router.navigate(['/' + USERS_PATHS.users_create]);
  }

  public onChangeRegions(regions) {
    const filterData = this.filterData;
    const ids = regions.map((region) => region.name);

    filterData.selected.regionsSelected = ids;
    filterData.params.mb_regions = ids;
    filterData.selected.branchesSelected = [];
    filterData.selected.sectorsSelected = [];
    filterData.data.branchesList = null;
    filterData.data.sectorsList = null;
    filterData.params.mb_ids = [];
    filterData.params.sectors_ids = [];
    this.getBranches();
    this.getSectors();
  }

  public onChangeBranches(branches) {
    const filterData = this.filterData;
    const ids = branches.map((branch) => branch.mb_id);

    filterData.selected.sectorsSelected = [];
    filterData.data.sectorsList = null;
    filterData.selected.branchesSelected = ids;
    filterData.params.mb_ids = ids;

    this.getSectors();
  }

  public onChangeSectors(sectors) {
    const filterData = this.filterData;
    const ids = sectors.map((sector) => sector.sector_id);

    filterData.selected.sectorsSelected = ids;
    filterData.params.sectors_ids = ids;
  }

  public onChangeCompanies(companies) {
    const filterData = this.filterData;
    const ids = companies.map((company) => company.campaign_id);

    filterData.selected.sectorsSelected = [];
    filterData.params.partnerships_ids = ids;
    filterData.selected.companiesSelected = ids;

    this.getSectors();
  }

  public onChangeRoles(roles): void {
    const filterData = this.filterData;
    const ids = roles.map((role) => role.id);

    filterData.selected.rolesSelected = ids;
    filterData.params.groups_ids = ids;
  }

  private onLoadPartial(paginationLoad: PaginationOnLoad) {
    this.getUsers(paginationLoad.offset, paginationLoad.limit);
  }

  public makeExportLink() {
    let exportLink = environment.apiPrefix + '/admin/users/export/?';
    const stateParams = cloneDeep(this.filterData.params);
    const params: string[] = [];

    each(stateParams, (value, key) => {
      if (Array.isArray(value)) {
        if (!value.length) {
          params.push(key + '=');
          return;
        }
        value.forEach((item) => {
          params.push(key + '=' + encodeURIComponent(item));
        });
        return;
      }
      params.push(key + '=' + encodeURIComponent(value));
    });

    return (exportLink += params.join('&'));
  }

  public exportUsers() {
    let link = this.makeExportLink();
    let a = document.createElement('a');
    a.href = link;
    a.target = '_self';
    a.download = link.split('/').pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private onApplyUsersFilter() {
    this.getUsers(0, this.paginationBlock.limitSelected);
  }

  private onResetUsersFilter() {
    this.emptyMessage = null;
    this.userDataIsLoaded = null;
    this.userListFiltered = null;
    this.filterData = cloneDeep(this.filterDataDefault);
    this.paginationBlock = cloneDeep(this.paginationBlockDefault);
    this.ngOnInit();
  }

  public onChangeUserExpanded(user) {
    if (user.state === 'open') {
      this.userExpanded = user.user;
      this.getUserExpandedData();
    } else {
      this.userExpanded = null;
    }
  }

  public makeEmptyMessage() {
    this.emptyMessage = Enums.tables['emptyDataMessage'];
  }
}
